import { czyWidziDostawy } from '@/lib/auth/dostepZrodel';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { InsightCard } from '@/components/InsightCard';
import { CoincidenceCard } from '@/components/CoincidenceCard';
import { ErrorState, InsightOutcome, LoadingState } from '@/components/states';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { Iskierka, ocenIskierke } from '@/components/wykresy/Iskierka';
import { WymagaUwagi, type PozycjaUwagi } from '@/components/WymagaUwagi';
import { PrzyciskDowodu, type Dowod } from '@/components/SzufladaDowodu';
import { problemSchoolsFrom } from '@/workspaces/drive/problemSchools';
import { formatCount, formatGrosze, formatRange } from '@/lib/format';
import { Pierscien } from '@/components/wykresy/Wykresy';
import { useAuth } from '@/lib/auth/AuthContext';
import {
  useDeliveryBySchool,
  useDeliverySummary,
  useDishSales,
  useSalesDeliveryCoincidence,
  useSchoolSales,
} from '@/lib/useAnalytics';
import { useDateRange } from '@/lib/useDateRange';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { klasyfikacjaDrive, swiezoscDrive } from '@/lib/pochodzenieDanych';
import { useOpisZrodla } from '@/lib/useTrybZrodla';

/**
 * HOME — migracja do języka „SYGNAŁ" (zaakceptowany kierunek C, R5-15-009).
 *
 * Pytanie ekranu jest to samo, co było: CO WARTO TERAZ ZOBACZYĆ. Zmienia się HIERARCHIA — jedna
 * liczba jest największa, reszta jej służy. Wcześniej cztery kafelki tej samej wielkości mówiły,
 * że wszystkie cztery są tak samo ważne; to jest nieprawda i kosztuje sekundę przy każdym
 * spojrzeniu.
 *
 * ŻADNYCH DANYCH DEMONSTRACYJNYCH. Ten ekran czyta wyłącznie realne API. Gdy sprzedaż w tym
 * środowisku jest pusta, pokazujemy pustkę — nie „przykładowe" liczby z makiety. Makieta stoi
 * pod osobnym adresem i tam wolno jej mieć dane wygenerowane.
 */
export function HomePage() {
  const opisZrodla = useOpisZrodla();
  const auth = useAuth();
  const [range] = useDateRange();
  const windowLabel = formatRange(range.date_from, range.date_to);

  const schools = useSchoolSales();
  const dishes = useDishSales();
  const delivery = useDeliverySummary();
  const bySchool = useDeliveryBySchool();
  const cross = useSalesDeliveryCoincidence();

  const driveConnected = czyWidziDostawy(auth);
  const loading = schools.isLoading || dishes.isLoading;
  const error = schools.error ?? dishes.error;

  const topInsight = dishes.data?.insights[0];
  const secondInsight = dishes.data?.insights[1];
  const problems = problemSchoolsFrom(bySchool.data?.schools ?? []);
  const coincidence = cross.data?.available === true ? cross.data.coincidences[0] : undefined;

  const liczbaZamowien = schools.data?.summary.total_orders ?? null;
  const liczbaSzkolWWyniku = schools.data?.schools.length ?? null;
  const punktualnosc = delivery.data?.current.on_time_pct ?? null;

  /*
   * ZERO TO NIE TO SAMO CO PUSTO. Reguła i jej uzasadnienie mieszkają w lib/pustyZbior.ts —
   * razem z testami, bo to jest niezmiennik całego produktu, a nie szczegół tego ekranu.
   * W skrócie: suma z pustego zbioru wynosi 0, więc API zwraca zero także wtedy, gdy nie było
   * czego liczyć. „Zero sprzedaży" to sygnał biznesowy, „puste źródło" to sygnał techniczny.
   */
  const ziarno = { wierszy: liczbaZamowien, bytow: liczbaSzkolWWyniku };
  const zrodloPuste = czyPustyZbior(ziarno);
  const wartoscSprzedazy = wartoscDoPokazania(schools.data?.summary.total_sales_grosze, ziarno);

  /*
   * Dowód jest budowany z TYCH SAMYCH pól, które zasilają liczbę na ekranie — nie z osobno
   * wpisanego opisu. To jest zabezpieczenie konstrukcyjne: przegląd konceptu wykrył wcześniej
   * ręcznie wpisane wartości, którym przeczył własny zbiór danych. Warstwa prezentacji nie może
   * być właścicielem liczb.
   */
  const dowodSprzedazy: Dowod = {
    tytul: 'Wartość sprzedaży (ceny katalogowe)',
    etykietaWartosci: 'Wartość wg cennika',
    wartosc: wartoscSprzedazy === null ? null : formatGrosze(wartoscSprzedazy),
    podstawa: { value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' },
    zrodlo: 'CRM /analytics/school-sales → crm_order_items, szkoła z transakcji',
    // Świeżość źródła sprzedaży jest w całym programie NIEUSTALONA. Wpisanie tu stempla odczytu
    // byłoby dokładnie tym błędem, który ekran „Jakość danych" nazywa największą otwartą luką.
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: windowLabel,
    oknoPorownawcze: dishes.data?.range.comparison
      ? formatRange(dishes.data.range.comparison.date_from, dishes.data.range.comparison.date_to)
      : null,
    /*
     * WZÓR PRZEPISANY Z BACKENDU, NIE Z PAMIĘCI.
     * Pierwsza wersja mówiła „Σ (cena × ilość)". To było zmyślone: crm/backend/lib/sales.js
     * liczy `COALESCE(SUM(oi.price_grosze), 0)` i NIGDZIE nie dotyka ilości — `price_grosze`
     * jest już wartością wiersza. Wiarygodnie brzmiący, nieprawdziwy szczegół w polu, którego
     * całym zadaniem jest dowodzić poprawności, jest gorszy niż brak tego pola.
     */
    wyliczenie: 'Σ price_grosze po pozycjach zamówień w oknie (bez mnożenia przez ilość — wartość wiersza jest już policzona w źródle)',
    szczegoly: [
      ['Przypisanie do szkoły', 'szkoła z TRANSAKCJI (stan z dnia zakupu)'],
      ['Zamówienia w oknie', formatCount(liczbaZamowien)],
      ['Szkoły w wyniku', formatCount(liczbaSzkolWWyniku)],
    ],
    proba: `${formatCount(liczbaZamowien)} zamówień`,
    ograniczenia: [
      ...(zrodloPuste
        ? [
            'ŹRÓDŁO ZWRÓCIŁO PUSTY ZBIÓR: zero pozycji zamówień i zero szkół w tym oknie. ' +
              'To NIE jest zmierzone zero — nie ma czego sumować. Sprzedaż mogła być, ale ' +
              'nie ma jej w tym źródle.',
          ]
        : []),
      'Kwota faktycznie pobrana nie jest tu sumowana — dla części transakcji nie istnieje w źródle.',
      'Świeżość źródła nieustalona: liczby są poprawne dla momentu odczytu, ale ten moment jest nieznany.',
    ],
    czegoNieWynika:
      'To NIE jest przychód. Wartość wg cennika liczy ceny przed zniżką szkolną; kwota faktycznie pobrana bywa niższa i dla części transakcji w ogóle nie jest zapisana. Nie wynika z tego również, że sprzedaż spadła lub wzrosła „naprawdę" — dopóki świeżość źródła jest nieustalona, nie wiadomo, czy porównujemy dwa równie aktualne odczyty.',
  };

  const dowodPunktualnosci: Dowod = {
    tytul: 'Dostawy na czas',
    etykietaWartosci: 'Udział dostaw w oknie',
    wartosc: punktualnosc === null ? null : `${punktualnosc}%`,
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: 'Drive /analytics/delivery-summary → utrwalony status dostawy',
    klasyfikacja: klasyfikacjaDrive(),
    swiezosc: swiezoscDrive(),
    oknoBiezace: windowLabel,
    wyliczenie: 'dostawy w oknie tolerancji ÷ dostawy zakończone',
    szczegoly: [['Dostawy w oknie', formatCount(delivery.data?.current.deliveries ?? null)]],
    jakoscDanych: delivery.data?.current.data_quality_reason ?? undefined,
    proba: `${formatCount(delivery.data?.current.deliveries ?? null)} dostaw`,
    czegoNieWynika:
      'Dostawa „przed czasem" nie jest tu sukcesem i nie wlicza się do punktualności — przyjazd przed otwarciem stołówki potrafi znaczyć, że jedzenie stało. Z tej liczby nie wynika też nic o przyczynie spóźnień: zbiór nie zawiera ani kierowcy, ani trasy.',
  };

  /*
   * „Wymaga uwagi" budujemy WYŁĄCZNIE z tego, co widać w danych. Żadnych dopisanych szczegółów
   * w rodzaju „wszystkie z jednej restauracji" — przegląd konceptu wykrył taki zmyślony detal
   * i to jest gorsze niż jego brak: brzmi wiarygodnie, więc ktoś na jego podstawie zadzwoni.
   */
  const uwagi: PozycjaUwagi[] = [];
  if (zrodloPuste) {
    uwagi.push({
      waga: 'krytyczna',
      tytul: 'Źródło sprzedaży nie zwróciło żadnych danych',
      detal: 'Zero pozycji i zero szkół w tym oknie — to stan integracji, nie wynik sprzedaży',
      cel: '/system',
    });
  }
  if (driveConnected && problems.length > 0) {
    uwagi.push({
      waga: 'krytyczna',
      tytul: `${problems.length} ${problems.length === 1 ? 'szkoła wymaga' : 'szkoły wymagają'} uwagi w dostawach`,
      detal: 'Punktualność poniżej progu przy wystarczającej próbie',
      cel: '/drive/szkoly',
    });
  }
  uwagi.push({
    waga: 'uwaga',
    tytul: 'Świeżość źródła sprzedaży nieustalona',
    detal: 'Nie wiadomo, z którego momentu pochodzą liczby sprzedaży',
    cel: '/system',
  });
  if (!driveConnected) {
    uwagi.push({
      waga: 'uwaga',
      tytul: 'Dostawy niepodłączone',
      detal: 'Bez nich nie da się policzyć zbieżności sprzedaży z punktualnością',
      cel: '/drive',
    });
  }

  return (
    <Box>
      <NaglowekEkranu
        tytul="Co warto teraz zobaczyć"
        opis={
          <>
            {opisZrodla}. Ekran nie pokazuje wszystkiego, co umiemy policzyć,
            tylko to, co dziś zmienia decyzję. Każda liczba prowadzi do swojego wyprowadzenia.
          </>
        }
        akcja={<DateRangeFilter wariant="naglowek" comparison={dishes.data?.range.comparison} />}
      />

      {loading ? <LoadingState /> : null}
      {error ? (
        <ErrorState
          error={error}
          context="Control Center czyta wyłącznie API. Gdy backend nie odpowiada albo sesja wygasła, nie pokazujemy liczb zastępczych."
        />
      ) : null}

      {!loading && !error ? (
        <Stack spacing={3} className="sygnal-kaskada">
          <PlytaGlowna
            kartaId="HOME-01"
            etykieta="Wartość sprzedaży (ceny katalogowe)"
            wartosc={wartoscSprzedazy === null ? null : formatGrosze(wartoscSprzedazy)}
            opis={
              <Typography variant="body2">
                {zrodloPuste
                  ? 'źródło zwróciło pusty zbiór w tym oknie — to nie jest zmierzone zero, tylko brak danych do policzenia'
                  : 'ceny katalogowe pozycji zamówienia, przed zniżką szkolną — to nie jest kwota pobrana'}
              </Typography>
            }
            zrodlo="CRM /analytics/school-sales → crm_order_items, szkoła z transakcji"
            okno={windowLabel}
            dowod={dowodSprzedazy}
            // Szuflada „Dowód" wewnątrz płyty ma WŁASNY adres (HOME-07); płyta jako karta nosi
            // HOME-01. Jeden atrybut na dwóch kartach wskazywałby ten sam element DOM.
            dowodId="HOME-07"
            trend={<KsztaltOkna punkty={schools.data?.charts?.daily_trend ?? null} />}
            boczne={
              <>
                <MiniMetryka kartaId="HOME-03" wartosc={formatCount(liczbaZamowien)} etykieta="zamówień" />
                <MiniMetryka
                  kartaId="HOME-04"
                  wartosc={punktualnosc === null ? null : String(punktualnosc)}
                  jednostka="%"
                  etykieta="dostawy na czas"
                />
                <MiniMetryka
                  kartaId="HOME-05"
                  wartosc={driveConnected ? formatCount(problems.length) : null}
                  etykieta="szkoły z uwagą"
                />
                <MiniMetryka kartaId="HOME-06" wartosc={formatCount(liczbaSzkolWWyniku)} etykieta="szkół w wyniku" />
              </>
            }
          />

          {/*
            KOLEJNOŚĆ KOLUMN JAK W MAKIECIE: narracja („Najważniejsze sygnały") po LEWEJ w 7/12,
            „Wymaga uwagi" po PRAWEJ w 5/12. Produkt miał to lustrzanie odbite, więc wzrok
            właściciela wchodził na ekran po innej przekątnej niż w zaakceptowanym kierunku.
          */}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '7fr 5fr' }, alignItems: 'start' }}>
            {/*
              OBA BLOKI W OBUDOWIE KARTY — jak w makiecie. Wcześniej leżały nagie na kanwie,
              przez co wiersze uwag w kolorze `--powierzchnia-2` stały na tle `--tlo` z kontrastem
              około 1,02:1: nie było widać, że to osobne, klikalne kafle.

              Poprzednia wersja tej poprawki miała gorszy błąd — „Wymaga uwagi" trafiło WEWNĄTRZ
              bloku sygnałów, więc siatka dostała jedno dziecko i prawa kolumna została pusta.
              Znalezione przez obejrzenie zrzutu, nie przez test: układ się nie wywraca, tylko
              wygląda na niedokończony.
            */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'baseline' }}>
                {/* Tytuł KARTY, nie ekranu: 16 px krojem marki. Ta sama skala co „Wymaga uwagi",
                    inaczej dwa sąsiadujące bloki krzyczą różnym głosem. */}
                <Typography
                  component="h2"
                  sx={{ fontFamily: 'var(--krój-marka)', fontWeight: 900, fontSize: 'var(--st-sredni)', letterSpacing: '-0.01em' }}
                >
                  Najważniejsze sygnały
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" component={Link} to={`/insights?from=${range.date_from}&to=${range.date_to}`}>
                  wszystkie insighty →
                </Button>
              </Stack>

              {!topInsight && !coincidence ? (
                // „Brak sygnału" i „brak danych" to dwie różne odpowiedzi — patrz InsightOutcome.
                <InsightOutcome
                  kartaId="HOME-11"
                  analysed={dishes.data?.dishes.length ?? 0}
                  window={{ date_from: range.date_from, date_to: range.date_to }}
                  subject="dań"
                />
              ) : (
                <Stack spacing={2}>
                  {coincidence && cross.data?.available === true ? (
                    <CoincidenceCard
                      kartaId="HOME-08"
                      item={coincidence}
                      window={cross.data.window}
                      comparison={cross.data.comparison}
                    />
                  ) : null}
                  {/* Dwa OSOBNE adresy, nie pętla: to są dwa nazwane wywołania (insights[0] i
                      insights[1]), więc każde dostaje swój identyfikator wprost. */}
                  {topInsight ? <InsightCard kartaId="HOME-09" insight={topInsight} /> : null}
                  {secondInsight ? <InsightCard kartaId="HOME-10" insight={secondInsight} /> : null}
                </Stack>
              )}
            </Paper>

            {/* Adres karty „Wymaga uwagi" (R5-15). WymagaUwagi jest komponentem wspólnym z ekranem
                „Jakość danych", więc identyfikator siada na TEJ obudowie — jedynym elementem tej
                karty, który należy do ekranu HOME. */}
            <Paper variant="outlined" data-cc-card-id="HOME-12" sx={{ p: 3 }}>
              <WymagaUwagi pozycje={uwagi} />
            </Paper>
          </Box>

          {/* Punktualność ma własny dowód, ale nie własną płytę — na ekranie może być tylko jedna
              liczba-bohater, inaczej hierarchia przestaje działać. */}
          {driveConnected && punktualnosc !== null ? (
            <>
            {/*
              KARTA Z PIERŚCIENIEM zamiast jednej linijki tekstu na dole ekranu.

              Makieta daje operacjom własną kartę z pierścieniem i kaflami; produkt miał tu zdanie
              „Dostawy na czas: 67,9% · 112 dostaw", ustawione pod całą resztą. Ta sama liczba,
              inna ranga — a punktualność jest jedyną wielkością na tym ekranie, która DZIŚ ma
              realne dane. Chowanie jej w przypisie było odwrotnością hierarchii.

              Dane bez zmian: ta sama wartość, to samo źródło, ten sam dowód. Zmienia się forma.
            */}
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack direction="row" spacing={3} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
                <Pierscien kartaId="HOME-13" wartosc={punktualnosc} etykieta="na czas" />
                <Box sx={{ minWidth: 220, flexGrow: 1 }}>
                  <Typography
                    component="h2"
                    sx={{ fontFamily: 'var(--krój-marka)', fontWeight: 900, fontSize: 'var(--st-sredni)', mb: 0.5 }}
                  >
                    Dostawy dziś
                  </Typography>
                  <Typography variant="body2" color="text.secondary" data-cc-card-id="HOME-14">
                    {formatCount(delivery.data?.current.deliveries ?? null)} dostaw · zmiana{' '}
                    {delivery.data?.on_time_delta_points ?? '—'} p.p. wobec poprzedniego okna
                  </Typography>
                  {/* Adres szuflady „Dowód" punktualności (R5-15): sam PrzyciskDowodu jest wspólny,
                      więc identyfikator siada na lokalnej obudowie, która trzyma wyłącznie ten dowód. */}
                  <Box data-cc-card-id="HOME-15" sx={{ ml: -1, mt: 1 }}>
                    <PrzyciskDowodu dowod={dowodPunktualnosci} etykieta="Dowód" />
                  </Box>
                </Box>
              </Stack>
            </Paper>
            </>
          ) : null}

          {!driveConnected ? (
            <Alert severity="info">
              Część obrazu jest niedostępna: dane o dostawach wymagają osobnego logowania do systemu
              kierowców. Bez tego nie da się też policzyć zbieżności sprzedaży z punktualnością.{' '}
              <Link to="/drive">Podłącz dostawy</Link>.
            </Alert>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}


/**
 * Kształt okna sprzedaży: iskierka serii dziennej plus zdanie o tym, co ona znaczy.
 *
 * CZEGO TU CELOWO NIE MA — i dlaczego to jest ważniejsze od tego, co jest.
 * Makieta pokazuje w tym miejscu procent zmiany („−3% wobec tego samego dnia tygodnia").
 * Produkt go NIE pokazuje, bo endpoint `school-sales` zwraca sumę wyłącznie dla okna
 * bieżącego; sumy okna porównawczego nie podaje. Policzenie różnicy wymagałoby albo
 * drugiego zapytania, albo zsumowania szeregu dziennego i udawania, że to to samo co
 * suma z bazy — a to dwie różne liczby, bo szereg bywa przycięty.
 *
 * Iskierka jest uczciwa: pokazuje PRZEBIEG wewnątrz okna i wyprowadza kierunek z samych
 * danych. Nie dostaje „w górę"/„w dół" jako deklaracji — dostaje liczby.
 */
function KsztaltOkna({ punkty }: { punkty: readonly { date: string; sales_grosze: number }[] | null }) {
  const wartosci = punkty ? punkty.map((p) => p.sales_grosze) : null;
  const ocena = ocenIskierke(wartosci);
  const czytelne =
    ocena.stan === 'ROSNACA' || ocena.stan === 'MALEJACA' || ocena.stan === 'PLASKA';

  return (
    <Stack
      /* Adres karty dla walidacji DOM (R5-15). Iskierka jest komponentem wspólnym, więc
         identyfikator siada na LOKALNYM korzeniu tej karty — obejmuje mikrowykres i zdanie,
         które razem niosą „kształt okna". Atrybut nie zmienia wyglądu ani zachowania. */
      data-cc-card-id="HOME-02"
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center', color: 'rgba(255,255,255,0.72)' }}
    >
      <Iskierka wartosci={wartosci} />
      <Typography variant="caption" sx={{ color: 'inherit' }}>
        {/*
          Opis własny, NIE `ocena.opis`. Komponent mówi „zmiana −31,8%", co przy takim
          sąsiedztwie czyta się jak „sprzedaż spadła o 31,8%" — a to jest zupełnie inna
          liczba: iskierka porównuje OSTATNI DZIEŃ z PIERWSZYM w oknie, nie okno z oknem.
          Zdanie musi nazwać, co z czym porównano, inaczej wykres podpowiada fałszywy wniosek.
        */}
        {czytelne
          ? `kształt okna dzień po dniu · ${punkty?.length ?? 0} dni w serii` +
            (ocena.zmianaPct === null
              ? ' · ostatni dzień wobec pierwszego: nieokreślone (pierwszy dzień ma zero)'
              : ` · ostatni dzień wobec pierwszego: ${ocena.zmianaPct > 0 ? '+' : ''}${ocena.zmianaPct.toFixed(1)}%`) +
            ' · to NIE jest zmiana okna wobec poprzedniego — źródło nie podaje jego sumy'
          : ocena.opis}
      </Typography>
    </Stack>
  );
}
